import { v4 as uuidv4 } from 'uuid';
import type { InitiateTransfertPayload } from '../types';
import {
  BASE_URL,
  buildQueryParams,
  fetchWithAuth,
  toNumber,
  toSearchParams,
} from './http';

export const fetchCountValue = async (): Promise<{ count: number } | null> => {
  try {
    const today = new Date();
    const oneMonthBefore = new Date();
    oneMonthBefore.setMonth(today.getMonth() - 3);
    const formatDate = (date: Date) => date.toISOString().split('.')[0];
    const queryParams = buildQueryParams({
      start_date: formatDate(oneMonthBefore),
      end_date: formatDate(today),
      type: 'payment',
    });
    const url = `${BASE_URL}/pay/v1/transactions/partners/me/count?${queryParams}`;
    const response = await fetchWithAuth(url, { method: 'GET', headers: {} });
    return response as { count: number };
  } catch (error) {
    console.error('fetchCountValue failed:', error);
    return null;
  }
};

export const fetchTransactions = async (): Promise<number | null> => {
  try {
    const today = new Date();
    const oneMonthBefore = new Date();
    oneMonthBefore.setMonth(today.getMonth() - 3);
    const formatDate = (date: Date) => date.toISOString().split('.')[0];
    const queryParams = buildQueryParams({
      type: 'payment',
      start_date: formatDate(oneMonthBefore),
      end_date: formatDate(today),
    });
    const url = `${BASE_URL}/pay/v1/transactions/partners/me/amount?${queryParams}`;
    const response = await fetchWithAuth(url, { method: 'GET', headers: {} });
    return typeof response === 'number' ? response : null;
  } catch (error) {
    console.error('fetchTransactions failed:', error);
    return null;
  }
};

export const createTransfert = async (
  payload: InitiateTransfertPayload
): Promise<unknown> => {
  try {
    const url = `${BASE_URL}/pay/v1/payouts?payment_type=bictorys`;
    const requestId = uuidv4();
    const response = await fetchWithAuth(
      url,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'idempotency-key': requestId,
        },
      },
      {},
      payload
    );
    return response as string | null | object;
  } catch (error) {
    console.error('createTransfert failed:', error);
    throw error;
  }
};

export const getTransactionsList = async (
  queryParams: Record<string, string | number>
): Promise<unknown> => {
  try {
    const url = `${BASE_URL}/pay/v1/transactions?${toSearchParams(queryParams).toString()}`;
    const traceId = uuidv4();
    const response = await fetchWithAuth(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Request-Id': traceId,
      },
    });
    return response;
  } catch (error) {
    console.error('getTransactionsList failed:', error);
    throw error;
  }
};

export const getTransfertTotal = async (
  queryParams: Record<string, string | number>
): Promise<number> => {
  try {
    const response = await fetchWithAuth(
      `${BASE_URL}/pay/v1/transactions/amount?type=transfer&${toSearchParams(queryParams).toString()}`,
      { method: 'GET' }
    );
    return toNumber(response);
  } catch (error) {
    console.error('getTransfertTotal failed:', error);
    return 0;
  }
};

export const getPaymentTotal = async (
  queryParams: Record<string, string | number>
): Promise<number> => {
  try {
    const response = await fetchWithAuth(
      `${BASE_URL}/pay/v1/transactions/amount?type=payment&${toSearchParams(queryParams).toString()}`,
      { method: 'GET' }
    );
    return toNumber(response);
  } catch (error) {
    console.error('getPaymentTotal failed:', error);
    return 0;
  }
};

export const getBillTotalAmount = async (
  queryParams: Record<string, string | number>
): Promise<number> => {
  try {
    const response = await fetchWithAuth(
      `${BASE_URL}/pay/v1/transactions/partners/me/amount?type=bill&${toSearchParams(queryParams).toString()}`,
      { method: 'GET' }
    );
    return toNumber(response);
  } catch (error) {
    console.error('getBillTotalAmount failed:', error);
    return 0;
  }
};

export const getMerchantKpi = async (
  queryParams: Record<string, string | number>
): Promise<number> => {
  try {
    const response = await fetchWithAuth(
      `${BASE_URL}/pay/v1/transactions/partners/me/amount?${toSearchParams(queryParams).toString()}`,
      { method: 'GET' }
    );
    return toNumber(response);
  } catch (error) {
    console.error('getMerchantKpi failed:', error);
    return 0;
  }
};
